import { MutationResult } from '@angular-architects/ngrx-toolkit';
import { CanDeactivateFn, RouterStateSnapshot } from '@angular/router';
import {
    FormUnsavedChangesWarningBase,
    SaveGuardCallback,
    unsavedChangesHandler,
    wasSaveSuccessful,
} from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { TemplateCriteria } from '@r1-compass-library/models';
import { Observable, of } from 'rxjs';
import { AllowPartialSave } from './allow-partial-save.interface';

/**
 * @description This was rolled out for template-criteria-builder's form because it is quite complex and was taking too much time
 * The issue that is blocking this from being reactive is that the form for now will just need to be re-assigned
 * as a new form that loses the reactive context at the class field level, but lifecycles like `ngOnInit` and
 * the DOM will have proper access. This general issue is why most of the refactors for the other components were
 * done in a way that did not re-assign the form group and lose the context.
 */
export interface TemplateCriteriaBuilderSaveGuardWarning
    extends FormUnsavedChangesWarningBase<'formUnsavedChangesWarningMisc'>,
        AllowPartialSave {
    onSave: () => Promise<MutationResult<TemplateCriteria>> | Promise<MutationResult<unknown>>;

    canDeactivate$: Observable<{
        dirtied: boolean;
        valid: boolean;
    }>;

    readonly allowPartialSave: boolean;
}

/**
 * @description Needs to wait on init to happen before form state can be tracked
 */
export const hasUnsavedChangesTemplateCriteria: CanDeactivateFn<TemplateCriteriaBuilderSaveGuardWarning> = (
    component: TemplateCriteriaBuilderSaveGuardWarning,
    _currentRoute,
    _routerStateSnapshot,
    nextState: RouterStateSnapshot
): Observable<boolean> => {
    const responseCallback = (response: SaveGuardCallback) => {
        if (response?.type === 'confirm') {
            return component.onSave().then(wasSaveSuccessful, () => false);
        }
        return response?.type === 'confirmWithoutSave' ? of(true) : of(false);
    };
    return unsavedChangesHandler(nextState, responseCallback, true, component.allowPartialSave);
};
